import { Box, Typography, keyframes } from "@mui/material";
import React, { RefObject } from "react";
import ReactMarkdown from "react-markdown";
import remarkGfm from "remark-gfm";
import { PureLightThemeCaaS } from "src/theme/schemes/PureLightTheme";
import styles from "../../content/pages/homepage.module.css";
import TypingDots from "../Loader/TypingDots";
import { useTypewriter } from "src/utils/useTypewriter";
import { ChatBubble } from "./ChatBubble";

const thinkingShimmer = keyframes`
  0%   { background-position: 100% 50%; }
  50%  { background-position: 0% 50%; }
  100% { background-position: 100% 50%; }
`;

type Props = {
  isWaitingForResponse: boolean;
  currentChatTranscript: string;
  currentUserTranscript: string;
  currentThoughtTitle: string;
  chatTypewriterSpeed: number;
  anchorRef: RefObject<HTMLDivElement | null>;
};

const ChatActiveTurn = ({
  isWaitingForResponse,
  currentChatTranscript,
  currentUserTranscript,
  currentThoughtTitle,
  chatTypewriterSpeed,
  anchorRef,
}: Props) => {
  const displayedCurrentChatTranscript = useTypewriter(currentChatTranscript, chatTypewriterSpeed);
  const displayedCurrentUserTranscript = useTypewriter(currentUserTranscript, chatTypewriterSpeed);

  const isCurrentChatTypingFinished =
    currentChatTranscript.length > 0 &&
    displayedCurrentChatTranscript.length === currentChatTranscript.length;

  let currentChatTextForMarkdown = displayedCurrentChatTranscript;
  let isCurrentChatStreamingTable = false;
  if (!isCurrentChatTypingFinished) {
    const lastBlockIndex = displayedCurrentChatTranscript.lastIndexOf("\n\n");
    if (lastBlockIndex !== -1) {
      const lastBlock = displayedCurrentChatTranscript.substring(lastBlockIndex + 2);
      if (lastBlock.includes("|")) {
        isCurrentChatStreamingTable = true;
        currentChatTextForMarkdown = displayedCurrentChatTranscript.substring(0, lastBlockIndex);
      }
    } else if (displayedCurrentChatTranscript.includes("|")) {
      isCurrentChatStreamingTable = true;
      currentChatTextForMarkdown = "";
    }
  }

  const showWaiting = isWaitingForResponse && !currentChatTranscript;
  const showStreaming = Boolean(currentChatTranscript);

  if (!showWaiting && !showStreaming) {
    return null;
  }

  // Single stable anchor Box so waiting → streaming does not remount the scroll target (avoids jump).
  return (
    <Box ref={anchorRef}>
      {showWaiting && (
        <>
          <ChatBubble role="user">
            {currentUserTranscript ? (
              <Typography
                variant="h5"
                sx={{
                  color: PureLightThemeCaaS.colors.alpha.white[100],
                  fontSize: "14px",
                  fontWeight: 500,
                  display: "block",
                }}
              >
                {currentUserTranscript}
              </Typography>
            ) : (
              <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 1, pt: 0.5 }}>
                <TypingDots />
              </Box>
            )}
          </ChatBubble>

          <ChatBubble role="assistant">
            <Box sx={{ display: "flex", alignItems: "center", justifyContent: "flex-start", gap: 1, pt: 0.5 }}>
              <Typography
                component="span"
                sx={{
                  textAlign: "center",
                  background:
                    "linear-gradient(90deg, #9e9e9e 0%, #9e9e9e 25%, #b0b0b0 35%, #d0d0d0 42%, #fff 50%, #d0d0d0 58%, #b0b0b0 65%, #9e9e9e 75%, #9e9e9e 100%)",
                  backgroundSize: "200% 100%",
                  backgroundClip: "text",
                  WebkitBackgroundClip: "text",
                  color: "transparent",
                  animation: `${thinkingShimmer} 2.4s ease-in-out infinite`,
                  "& *": {
                    color: "inherit",
                    background: "inherit",
                    backgroundClip: "inherit",
                    WebkitBackgroundClip: "inherit",
                  },
                }}
              >
                <ReactMarkdown remarkPlugins={[remarkGfm]} components={{ p: ({ children }) => <span>{children}</span> }}>
                  {currentThoughtTitle || "thinking"}
                </ReactMarkdown>
              </Typography>
            </Box>
          </ChatBubble>
        </>
      )}

      {showStreaming && (
        <>
          <ChatBubble role="user">
            <Typography
              variant="h5"
              sx={{
                color: PureLightThemeCaaS.colors.alpha.white[100],
                fontSize: "14px",
                fontWeight: 500,
                display: "block",
              }}
            >
              {displayedCurrentUserTranscript}
            </Typography>
          </ChatBubble>

          <ChatBubble role="assistant">
            <Typography
              variant="h4"
              fontSize="16px"
              color={`${PureLightThemeCaaS.colors.alpha.white[100]}`}
              p={0}
              pl={0}
            >
              <ReactMarkdown
                className={`${styles.reactMarkdownStart} ${styles.noPaddingOverride}`}
                remarkPlugins={[remarkGfm]}
              >
                {currentChatTextForMarkdown}
              </ReactMarkdown>
            </Typography>
          </ChatBubble>

          {isCurrentChatStreamingTable && (
            <Box sx={{ display: "flex", justifyContent: "center", py: 2 }}>
              <Typography sx={{ color: "grey", fontStyle: "italic", textAlign: "center" }}>
                Generating response...
              </Typography>
            </Box>
          )}
        </>
      )}
    </Box>
  );
};

export default ChatActiveTurn;
